import _ from 'lodash'
import {CSS_PROPERTIES_TO_SCHEMA_PATH, DEFAULT_SERIALIZER_TYPE} from './metadata'
import serializers from './serializers'
import {
    AllowedCssAtRules,
    CSS_DEFAULT_DECLARATION_PROPERTY_FORMAT_RESERVED_CHARS,
    CSS_DELIMITER,
    CssAst,
    CssBlock,
    CssDeclarationBlock,
    CssFile,
    CssProperty,
    CssSelector,
    CssValue,
    DATA_TYPES,
    DEFAULT_BREAKPOINTS_MIN_RANGE
} from './constants'
import {parse} from './peg-css'
import {isCssRule} from './utils'
import {createCharReplacementFunc} from '../../utils/utils'
import type {PartialComponentExport} from '../constants'
import type {PlainObject, ResolvedDataItem, WmlFileContent} from '@wix/document-services-types'
import {stripHashIfExists} from '../../../utils/dataUtils'
import {ADDITIONAL_METADATA, METADATA, type PropertyMetadata} from '../../../metadata'
import type {DecoratedExportFormat, VariantData, VariantsMap, VariantType} from '../../../types'

type PartialComponents = Partial<DecoratedExportFormat['components']>

interface ParsedSelector {
    component: string
    variants: string[]
}

const DEFAULT_PROPERTY_METADATA = {types: [{type: DEFAULT_SERIALIZER_TYPE}]}

const getDataItemType = (declarations: CssDeclarationBlock, namespace: string): string =>
    declarations[`${namespace}-type`] as string

const replaceReservedPropertyCharsReplacements = createCharReplacementFunc(
    _.invert(CSS_DEFAULT_DECLARATION_PROPERTY_FORMAT_RESERVED_CHARS)
)

const getDefaultPropertyPath = (namespace: string, cssProperty: string): string | undefined => {
    const [propertyNamespace, ...propertyPath] = cssProperty
        .split(CSS_DELIMITER)
        .map(replaceReservedPropertyCharsReplacements)

    if (propertyNamespace !== namespace) {
        return undefined
    }

    return propertyPath.join('.')
}

const getPropertyPathByCssPath = (namespace: string, dataItemType: string, cssProperty: string): string | undefined =>
    CSS_PROPERTIES_TO_SCHEMA_PATH[namespace]?.[dataItemType]?.[cssProperty] ??
    getDefaultPropertyPath(namespace, cssProperty)

const getPropertyMetadata = (namespace: string, dataItemType: string, cssProperty: string): PropertyMetadata =>
    METADATA[namespace]?.[dataItemType]?.[cssProperty] ?? DEFAULT_PROPERTY_METADATA

const getFallbackValue = (value: any): CssValue => {
    return serializers[DEFAULT_SERIALIZER_TYPE].deserialize(value)
}

const deserializeValue = (value: CssValue, types: PropertyMetadata['types']): any => {
    for (const typeMetadata of types) {
        const {type} = typeMetadata
        const serializer = (serializers as Record<string, any>)[type]
        const deserializedValue = serializer.deserialize(
            value,
            {...typeMetadata, ...ADDITIONAL_METADATA[type]},
            deserializeValue
        )

        if (deserializedValue !== undefined) {
            return deserializedValue
        }
    }

    return getFallbackValue(value)
}

const generateSetDataItemFunc = (components: PartialComponents, component: string, variants: string[]) => {
    return (dataItem: ResolvedDataItem, namespace: string) => {
        const defaultPath = [component, namespace, 'default']
        const variantsArrPath = [component, namespace, 'variants']
        if (!_.has(components, variantsArrPath)) {
            _.set(components, variantsArrPath, [])
        }

        if (variants.length > 0) {
            const variantsArr = _.get(components, variantsArrPath)
            variantsArr.push({
                value: dataItem,
                conditions: variants.map(variant => `#${variant}`)
            })
        } else {
            _.set(components, defaultPath, dataItem)
        }
    }
}

const parseSelector = (selector: CssSelector): ParsedSelector => {
    const [component, ...variants] = selector.split('.')

    return {
        component: stripHashIfExists(component),
        variants
    }
}

const atRuleToVariantMapItem: Record<
    AllowedCssAtRules,
    {key: VariantType; buildValue(queries: PlainObject): VariantData}
> = {
    preset: {
        key: 'presets',
        buildValue: ({id}: PlainObject) => ({
            id
        })
    },
    media: {
        key: 'breakpoints',
        buildValue: ({id, maxWidth, canvasSize}: PlainObject) => ({
            id,
            max: maxWidth,
            min: DEFAULT_BREAKPOINTS_MIN_RANGE,
            canvasSize
        })
    }
}

const insertAtRule = (identifier: AllowedCssAtRules, queries: PlainObject, variantsMap: VariantsMap): string => {
    const {id} = queries
    const {key, buildValue} = atRuleToVariantMapItem[identifier]
    _.set(variantsMap, [key, id], buildValue(queries))
    return id
}

export const getDataItemByDeclarations = (declarations: CssDeclarationBlock, namespace: string): ResolvedDataItem => {
    const dataItem = {}
    const dataItemType = getDataItemType(declarations, namespace)
    _.forOwn(declarations, (value: CssValue, cssProperty: CssProperty) => {
        const propertyPath = getPropertyPathByCssPath(namespace, dataItemType, cssProperty)
        if (!propertyPath) {
            return
        }

        const {types} = getPropertyMetadata(namespace, dataItemType, propertyPath)
        const deserializedValue = deserializeValue(value, types)
        _.set(dataItem, propertyPath, deserializedValue)
    })

    return dataItem
}

export const getDataItemsByDeclarations = (declarations: CssDeclarationBlock): Record<string, ResolvedDataItem> => {
    const dataItems: Record<string, ResolvedDataItem> = {}
    for (const namespace of DATA_TYPES) {
        const dataItem = getDataItemByDeclarations(declarations, namespace)
        if (!_.isEmpty(dataItem)) {
            dataItems[namespace] = dataItem
        }
    }

    return dataItems
}

export const setComponentStructureDataItems = (
    components: PartialComponents,
    component: string,
    variants: string[],
    declarations: CssDeclarationBlock
) => {
    const dataItems = getDataItemsByDeclarations(declarations)
    const setDataItem = generateSetDataItemFunc(components, component, variants)
    _.forOwn(dataItems, setDataItem)
}

export const parseBlocks = (
    blocks: CssBlock[],
    components: PartialComponents,
    variantsMap: VariantsMap,
    outerVariants: string[] = []
) => {
    for (const block of blocks) {
        if (isCssRule(block)) {
            const {selector, declarations} = block
            const {component, variants} = parseSelector(selector)
            setComponentStructureDataItems(components, component, [...outerVariants, ...variants], declarations)
        } else {
            const {identifier, queries, rules} = block
            const atRuleId = insertAtRule(identifier, queries, variantsMap)
            parseBlocks(rules, components, variantsMap, [atRuleId])
        }
    }
}

export const parseCssAst = (cssAst: CssAst): PartialComponentExport['style'] => {
    const res: Required<PartialComponentExport['style']> = {
        components: {},
        variantsMap: {}
    }
    parseBlocks(cssAst, res.components, res.variantsMap)
    return res
}

export const parseCssToAst = (cssFile: WmlFileContent): CssAst => parse(cssFile, {})

export const parseCss = (cssFile: WmlFileContent): PartialComponentExport['style'] =>
    parseCssAst(parseCssToAst(cssFile))

export const parseCssFile = ({content}: CssFile): PartialComponentExport['style'] => parseCss(content)
